(() => {
  'use strict';

  if (window.__htStarterAddonCartLoaded) return;
  window.__htStarterAddonCartLoaded = true;

  const ADDON_VARIANT_ID = 42741028225191;
  const CART_URL = '/cart.js';
  const CART_ADD_URL = '/cart/add.js';
  const CART_UPDATE_URL = '/cart/update.js';

  let reconcilePromise = null;
  let addPromise = null;

  const fetchConfig = (body) => ({
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  const getCart = async () => {
    const response = await fetch(`${CART_URL}?t=${Date.now()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Unable to load cart (${response.status})`);
    return response.json();
  };

  const cartState = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return {
      qualifyingItemPresent: items.some((item) => Number(item.variant_id) !== ADDON_VARIANT_ID),
      addonPresent: items.some((item) => Number(item.variant_id) === ADDON_VARIANT_ID),
      addonItems: items.filter((item) => Number(item.variant_id) === ADDON_VARIANT_ID)
    };
  };

  const updateCartCounts = (cart) => {
    document.querySelectorAll('cart-count').forEach((count) => {
      count.textContent = String(cart.item_count || 0);
      count.toggleAttribute('hidden', !cart.item_count);
      count.setAttribute('aria-label', `${cart.item_count || 0} items in cart`);
    });
  };

  const updateOfferBoxes = (cart) => {
    const state = cartState(cart);
    const eligible = state.qualifyingItemPresent && !state.addonPresent;

    document.querySelectorAll('[data-ht-starter-addon-offer]').forEach((box) => {
      box.hidden = !eligible;
      const button = box.querySelector('[data-ht-starter-addon-add]');
      const text = box.querySelector('[data-ht-starter-addon-button-text]');
      const message = box.querySelector('[data-ht-starter-addon-message]');
      if (button) button.disabled = Boolean(addPromise);
      if (text && !addPromise) text.textContent = text.dataset.defaultText || text.textContent.trim();
      if (message && !addPromise) message.textContent = '';
    });

    updateCartCounts(cart);
  };

  const refreshThemeCart = (openDrawer = false) => {
    if (window.location.pathname === '/cart') {
      window.location.reload();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { open: openDrawer }
    }));
  };

  const showTemporaryNotice = (message) => {
    let notice = document.querySelector('[data-ht-starter-addon-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.setAttribute('data-ht-starter-addon-notice', '');
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.style.cssText = 'position:fixed;left:50%;bottom:22px;z-index:99999;width:min(92vw,420px);transform:translateX(-50%);padding:12px 16px;border-radius:8px;background:#111;color:#fff;font:700 13px/1.35 Arial,sans-serif;text-align:center;box-shadow:0 8px 24px rgba(0,0,0,.28);';
      document.body.appendChild(notice);
    }
    notice.textContent = message;
    notice.hidden = false;
    window.clearTimeout(notice.__htTimer);
    notice.__htTimer = window.setTimeout(() => { notice.hidden = true; }, 4500);
  };

  const removeOrphanAddon = async (cart, reason = 'reconcile') => {
    const state = cartState(cart);
    if (state.qualifyingItemPresent || !state.addonPresent) return cart;

    const updates = {};
    state.addonItems.forEach((item) => { updates[item.key] = 0; });

    const response = await fetch(CART_UPDATE_URL, fetchConfig({ updates }));
    const updatedCart = await response.json();
    if (!response.ok || updatedCart.errors) {
      throw new Error(updatedCart.description || updatedCart.message || 'Unable to remove dependent add-on');
    }

    updateOfferBoxes(updatedCart);
    refreshThemeCart(false);

    if (reason !== 'initial-load') {
      showTemporaryNotice('The 60-night add-on was removed because it requires another item in your cart.');
    }

    document.dispatchEvent(new CustomEvent('ht:starter-addon-removed', {
      detail: { cart: updatedCart, reason }
    }));

    return updatedCart;
  };

  const reconcile = (reason = 'update') => {
    if (reconcilePromise) return reconcilePromise;

    reconcilePromise = (async () => {
      try {
        let cart = await getCart();
        cart = await removeOrphanAddon(cart, reason);
        updateOfferBoxes(cart);
        return cart;
      }
      catch (error) {
        console.error('[HT Starter Add-On]', error);
        return null;
      }
      finally {
        reconcilePromise = null;
      }
    })();

    return reconcilePromise;
  };

  const addAddon = async (button) => {
    if (addPromise) return addPromise;

    addPromise = (async () => {
      const box = button.closest('[data-ht-starter-addon-offer]');
      const text = box?.querySelector('[data-ht-starter-addon-button-text]');
      const message = box?.querySelector('[data-ht-starter-addon-message]');

      try {
        button.disabled = true;
        if (text) text.textContent = 'Adding...';
        if (message) message.textContent = '';

        const cart = await getCart();
        const state = cartState(cart);

        if (!state.qualifyingItemPresent) {
          updateOfferBoxes(cart);
          throw new Error('Add another product to your cart before adding this 60-night offer.');
        }

        if (state.addonPresent) {
          updateOfferBoxes(cart);
          return cart;
        }

        const response = await fetch(CART_ADD_URL, fetchConfig({
          items: [{
            id: ADDON_VARIANT_ID,
            quantity: 1,
            properties: {
              '_Cart Add-On': '60 Nights'
            }
          }]
        }));
        const result = await response.json();
        if (!response.ok || result.errors) {
          throw new Error(result.description || result.message || 'Unable to add the 60-night offer');
        }

        const updatedCart = await getCart();
        updateOfferBoxes(updatedCart);
        refreshThemeCart(true);
        document.dispatchEvent(new CustomEvent('ht:starter-addon-added', {
          detail: { cart: updatedCart }
        }));
        return updatedCart;
      }
      catch (error) {
        console.error('[HT Starter Add-On]', error);
        if (message) message.textContent = error.message;
        if (text) text.textContent = 'Try Again';
        button.disabled = false;
        return null;
      }
      finally {
        addPromise = null;
      }
    })();

    return addPromise;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ht-starter-addon-add]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    addAddon(button);
  });

  document.addEventListener('cart:updated', () => {
    window.setTimeout(() => reconcile('cart-updated'), 50);
  });

  window.addEventListener('pageshow', () => reconcile('initial-load'));
  document.addEventListener('DOMContentLoaded', () => reconcile('initial-load'));
})();
